import styled from "@emotion/styled"
import { ChevronLeft } from "@mui/icons-material" // Import ChevronLeft icon
import { IconButton, Input, Modal } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useLocale } from "next-intl"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useState } from "react"
import {
  Culture,
  fetchAISearchResults,
  getProjectsPath,
  mapAISearchResults,
  type SearchResultItem,
} from "utils"
import { CloseCircleIcon, XIcon } from "../icons"
import { useIsDesktop } from "../common/bit/MediaQueries"
import Results from "./Results"

import { useTheme } from "@emotion/react"
import { useEffect } from "react"

const isClassicSearch = process.env.ENABLE_CLASSIC_SEARCH === "true"

const StyledContainer = styled.div`
  position: relative;
`

const StyledModalBox = styled.div<{ hasBottomRadius: boolean }>`
  border-radius: ${(props) => (props.hasBottomRadius ? "16px 16px 0px 0px" : "16px")};
  position: absolute;
  top: 106px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100vw - 32px);
  height: 72px;
  background-color: white;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
  padding: 16px;
  margin: 0 auto;
  input {
    padding: 0px;
    height: 100%;
    &::placeholder {
      color: #6f6f6f;
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    top: 140px;
    width: 1040px;
  }
`

const StyledCloseIcon = styled.div`
  position: absolute;
  top: 70px;
  right: 50%;
  transform: translateX(50%);
  width: calc(100vw - 32px);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;

  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    top: 105px;
    width: 1040px;
  }
  button {
    padding: 0px;
  }
  svg {
    width: 28px;
    height: 28px;
  }
`

type Props = {
  open: boolean
  handleClose: () => void
  culture: Culture
  pathString: string
  idNumber: string[]
  searchRouteString: string
}

type Result = {
  items: []
}
const fetchSearchResults = async (text: string, path: string) => {
  const lowerCaseText = text.toLowerCase()
  // Replace this with the actual API call
  const GRAPHQL_URL = process.env.GRAPHQL_URL?.replace("/graphql", "")
  const fetchPages = fetch(`${GRAPHQL_URL}/generic/search?path=${path}&term=${lowerCaseText}`)
  const fetchProjects = fetch(
    `${GRAPHQL_URL}/generic/search?path=${getProjectsPath()}&term=${lowerCaseText}&pageSize=1000`,
  )
  const [pagesJSON, projectsJSON] = await Promise.all([fetchPages, fetchProjects])
  const pages: Result = await pagesJSON.json()
  const projects: Result = await projectsJSON.json()

  return { pages, projects }
}

export const SearchModal = ({
  open,
  handleClose,
  culture,
  idNumber,
  pathString,
  searchRouteString,
}: Props) => {
  const theme = useTheme()
  const [text, setText] = useState<string>("")
  const [debouncedText, setDebouncedText] = useState<string>(text)
  const close = () => {
    handleClose()
    setText("")
    setDebouncedText("")
  }
  const clear = () => {
    setText("")
    setDebouncedText("")
  }
  const locale = useLocale()

  const pathname = usePathname()
  const desktop = useIsDesktop()

  useEffect(() => {
    close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Classic Umbraco search (used when ENABLE_CLASSIC_SEARCH is set)
  const { data: classicData, isFetching: classicFetching } = useQuery({
    queryKey: ["search", debouncedText],
    queryFn: () => fetchSearchResults(debouncedText, pathString),
    enabled: !!debouncedText && isClassicSearch,
    refetchOnWindowFocus: false,
  })

  // AI search (default, used when ENABLE_CLASSIC_SEARCH is not set)
  const { data: aiData, isFetching: aiFetching } = useQuery({
    queryKey: ["ai-search", debouncedText],
    queryFn: () => fetchAISearchResults(debouncedText, pathString),
    enabled: !!debouncedText && !isClassicSearch,
    refetchOnWindowFocus: false,
  })

  const pageIDs = isClassicSearch
    ? ((classicData?.pages?.items?.map((el: any) => el.id) as number[]) ?? [])
    : []
  const projectIDs = isClassicSearch
    ? ((classicData?.projects?.items?.map((el: any) => el.id) as number[]) ?? [])
    : []
  const isFetching = isClassicSearch ? classicFetching : aiFetching
  const aiMappedResults: SearchResultItem[] | null =
    !isClassicSearch && aiData ? mapAISearchResults(aiData, desktop, locale) : null

  return (
    <Modal open={open} onClose={close}>
      <StyledContainer>
        <StyledCloseIcon>
          <IconButton onClick={() => close()} aria-label="לחצן סגירה">
            <CloseCircleIcon />
          </IconButton>
        </StyledCloseIcon>
        <StyledModalBox hasBottomRadius={!!debouncedText}>
          <Input
            onChange={(e) => setText(e.target.value)}
            value={text}
            sx={{
              "&.MuiInput-root:before": {
                borderBottom: "none",
              },
              "&.MuiInput-root:after": {
                borderBottom: "none",
              },
              "&.MuiInput-root:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
              },
            }}
            name={locale === "he" ? "חיפוש" : "Search"}
            inputProps={{
              "aria-label": locale === "he" ? "הקלד מילות חיפוש" : "Type search words",
            }}
            fullWidth
            placeholder={locale === "he" ? "חיפוש" : "Search"}
            style={{ height: "40px", padding: 0 }}
            startAdornment={
              debouncedText ? (
                <IconButton onClick={clear}>
                  <XIcon />
                </IconButton>
              ) : (
                <div
                  style={{
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Image src="/searchicon.png" alt="Search" width={24} height={24} />
                </div>
              )
            }
            endAdornment={
              <IconButton
                style={{
                  background: theme.colors.propBlue,
                  color: "white",
                  transform: locale === "en" ? "rotate(180deg)" : "none",
                }}
                aria-label="חפש"
                onClick={() => setDebouncedText(text)}
              >
                <ChevronLeft color="inherit" />
              </IconButton>
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setDebouncedText(text)
              }
            }}
          />
        </StyledModalBox>
        {debouncedText?.length > 0 ? (
          <Results
            pageIDs={pageIDs}
            projectIDs={projectIDs}
            handleClose={close}
            culture={culture}
            isLoadingQuery={isFetching}
            word={debouncedText}
            idNumber={idNumber}
            searchRouteString={searchRouteString}
            mappedResults={aiMappedResults}
            isAISearch={!isClassicSearch}
          />
        ) : null}
      </StyledContainer>
    </Modal>
  )
}
